// r24 Product Catalog — Contact-only CTA (phone / LINE / email).
// No cart. Sticky on mobile, inline on desktop. Touch targets ≥ 44×44.

import { Phone, MessageCircle, Mail } from "lucide-react";
import { BRAND_PHONE, BRAND_EMAIL } from "@/lib/brand";
import type { NaatiProduct } from "@/data/naati-products";

const LINE_URL = "https://line.me/ti/p/@nycli";

interface Props {
  product: NaatiProduct;
  locale: "th" | "en";
  variant?: "inline" | "sticky-mobile";
}

export default function NaatiProductCTA({ product, locale, variant = "inline" }: Props) {
  const isTh = locale === "th";
  const name = isTh ? product.nameTh : product.nameEn;
  const subject = encodeURIComponent(
    isTh ? `สั่งแปล NAATI: ${name} (${product.sku})` : `NAATI order: ${name} (${product.sku})`,
  );
  const body = encodeURIComponent(
    isTh
      ? `สวัสดีค่ะ/ครับ\nต้องการสั่งแปล NAATI: ${name}\nSKU: ${product.sku}\nจำนวนเอกสาร: \nวันที่ต้องการรับ: \n(แนบไฟล์สแกนได้เลย)`
      : `Hello,\nI would like to order NAATI translation for: ${name}\nSKU: ${product.sku}\nNumber of documents: \nRequired by: \n(Please find scans attached)`,
  );
  const lineText = encodeURIComponent(
    isTh ? `สั่งแปล NAATI: ${name} (${product.sku})` : `NAATI order: ${name} (${product.sku})`,
  );

  const btnBase = "inline-flex items-center justify-center gap-2 rounded-xl font-semibold shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md min-h-[44px] px-4 py-3 text-sm";
  const labels = isTh
    ? { call: "โทรสั่งซื้อ", line: "แชท LINE", email: "ส่งอีเมล" }
    : { call: "Call to order", line: "Chat on LINE", email: "Email us" };

  if (variant === "sticky-mobile") {
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-3 gap-1.5 border-t border-slate-200 bg-white/95 p-2 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur md:hidden"
        role="group"
        aria-label={isTh ? "ช่องทางสั่งซื้อ" : "Order channels"}
      >
        <a href={`tel:${BRAND_PHONE.replace(/[^+\d]/g, "")}`}
          className={`${btnBase} bg-emerald-600 text-white`}
          data-cta="phone" aria-label={labels.call}>
          <Phone className="h-4 w-4" /> <span className="text-[13px]">{labels.call}</span>
        </a>
        <a href={`${LINE_URL}?text=${lineText}`} target="_blank" rel="noopener"
          className={`${btnBase} bg-[#06C755] text-white`}
          data-cta="line" aria-label={labels.line}>
          <MessageCircle className="h-4 w-4" /> <span className="text-[13px]">LINE</span>
        </a>
        <a href={`mailto:${BRAND_EMAIL}?subject=${subject}&body=${body}`}
          className={`${btnBase} bg-slate-800 text-white`}
          data-cta="email" aria-label={labels.email}>
          <Mail className="h-4 w-4" /> <span className="text-[13px]">Email</span>
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={isTh ? "ช่องทางสั่งซื้อ" : "Order channels"}>
      <a href={`tel:${BRAND_PHONE.replace(/[^+\d]/g, "")}`}
        className={`${btnBase} bg-emerald-600 text-white hover:bg-emerald-700`}
        data-cta="phone">
        <Phone className="h-4 w-4" /> {labels.call} <span className="opacity-80">{BRAND_PHONE}</span>
      </a>
      <a href={`${LINE_URL}?text=${lineText}`} target="_blank" rel="noopener"
        className={`${btnBase} bg-[#06C755] text-white hover:brightness-95`}
        data-cta="line">
        <MessageCircle className="h-4 w-4" /> {labels.line}
      </a>
      <a href={`mailto:${BRAND_EMAIL}?subject=${subject}&body=${body}`}
        className={`${btnBase} bg-slate-800 text-white hover:bg-slate-900`}
        data-cta="email">
        <Mail className="h-4 w-4" /> {labels.email}
      </a>
    </div>
  );
}
